import { Container } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SuccessSubscriptionContainer } from 'components/Modals/implementations/SuccessSubscription';
import { analytics, events } from 'services/analytics';
import useSubscription from 'services/hooks/subscription/useSubscription';
import { locations } from 'services/routes/locations';

interface CheckoutProps {
  shouldBlockRedirect?: boolean;
}

/**
 * Component to show when users come back to our page from the stripe checkout flow.
 *
 * @param shouldBlockRedirect If true, closes the modal and stays on page on modal exiting instead of redirecting.
 */
const Checkout = ({ shouldBlockRedirect }: CheckoutProps) => {
  const navigate = useNavigate();
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  const { subscriptionType } = useSubscription();
  const [searchParams] = useSearchParams();

  const handleCheckoutFlow = useCallback(() => {
    if (searchParams.has('success')) {
      analytics.track(events.purchaseSuccess(subscriptionType || null));
      setSubscriptionSuccess(true);

      return;
    }

    if (searchParams.has('canceled')) {
      analytics.track(events.purchaseCancel(subscriptionType || null));

      if (!shouldBlockRedirect) {
        navigate(locations.subscription.pricing.pathname(''));
      }
    }
  }, [navigate, searchParams, subscriptionType, shouldBlockRedirect]);

  useEffect(() => {
    if (searchParams.has('distribution_upsell_flow')) {
      setSubscriptionSuccess(true);
      return;
    }
    handleCheckoutFlow();
  }, [navigate, handleCheckoutFlow, subscriptionType, searchParams]);

  return (
    <Container>
      {subscriptionSuccess && (
        <SuccessSubscriptionContainer
          shouldBlockRedirect={shouldBlockRedirect}
        />
      )}
    </Container>
  );
};

export default Checkout;
